import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import Tag from 'src/components/primitives/Tag/Tag';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import {
	FeeTierTableRow,
	TakerFeeGroup,
} from 'src/components/Modals/FeeTierModal/types';
import { cj } from 'src/utils/classNames';

interface Props {
	tableRows: FeeTierTableRow[];
	takerFeeGroups: TakerFeeGroup[];
	/** The rung the user is charged at, which a promo floor can lift above their volume tier. */
	userFeeTierIndex: number;
	/** True while a promo is charging this account above the rung its volume earns. */
	isPromoLiftingUserTier: boolean;
}

interface FeeTierDataRow extends FeeTierTableRow {
	isActive: boolean;
	isPromoLifting: boolean;
}

const BODY_CELL_CLASSNAME = 'h-auto';

// Measured against the rendered table. Headers are `whitespace-nowrap`, so a
// column can never be narrower than its own heading. The two fee columns are
// held equal so the pair reads as one comparison rather than a ranking. Tier
// is wider than the heading so the You / Promo chip still fits beside the name.
const COLUMN_WIDTH = {
	tier: 108,
	volume: 96,
	takerFee: 108,
	makerFee: 88,
};

const numericColumnMeta = (fixedPx: number) => ({
	width: { fixedPx },
	headerCellClassName: 'text-right',
	bodyCellClassName: cj('text-right', BODY_CELL_CLASSNAME),
});

const columnHelper = createColumnHelper<FeeTierDataRow>();

const tierColumn = columnHelper.accessor('tier', {
	header: 'Tier',
	meta: {
		width: { fixedPx: COLUMN_WIDTH.tier },
		bodyCellClassName: cj('text-left whitespace-nowrap', BODY_CELL_CLASSNAME),
	},
	cell: (info) => {
		const row = info.row.original;
		return (
			<span className="inline-flex items-center gap-x-xs">
				{row.isActive &&
					(row.isPromoLifting ? (
						<Tooltip
							decoration="cursor-help"
							content="Every account is on the promotional fee tier for a limited time."
						>
							<Tag variant="new">Promo</Tag>
						</Tooltip>
					) : (
						<Tag variant="neutral">You</Tag>
					))}
				{info.getValue()}
			</span>
		);
	},
});

const volumeColumn = columnHelper.accessor('volume', {
	header: '30D Volume',
	meta: numericColumnMeta(COLUMN_WIDTH.volume),
	cell: (info) => info.getValue(),
});

const makerColumn = columnHelper.accessor('makerFee', {
	header: () => (
		<DefinitionTooltip definitionId="feeTierMakerRebate">
			Maker
		</DefinitionTooltip>
	),
	meta: numericColumnMeta(COLUMN_WIDTH.makerFee),
	cell: (info) => info.getValue(),
});

export default function FeeTierModalTable({
	tableRows,
	takerFeeGroups,
	userFeeTierIndex,
	isPromoLiftingUserTier,
}: Props) {
	const columns = useMemo(() => {
		const takerColumns = takerFeeGroups.map((group, index) =>
			columnHelper.accessor((row) => row.takerFees[index], {
				id: `taker-${group.label}`,
				header: group.label,
				meta: numericColumnMeta(COLUMN_WIDTH.takerFee),
				cell: (info) => info.getValue(),
			})
		);

		return [tierColumn, volumeColumn, ...takerColumns, makerColumn];
	}, [takerFeeGroups]);

	const data = useMemo<FeeTierDataRow[]>(
		() =>
			tableRows.map((row) => ({
				...row,
				isActive: row.tierIndex === userFeeTierIndex,
				isPromoLifting: isPromoLiftingUserTier,
			})),
		[tableRows, userFeeTierIndex, isPromoLiftingUserTier]
	);

	return (
		<DataTable
			columns={columns}
			data={data}
			fitContent
			className="w-full table-fixed"
			containerClassName="h-auto overflow-x-auto rounded-sm border border-stroke-divider sm:overflow-x-clip"
			getRowClassName={(row) =>
				row.isActive ? 'bg-button-selector hover:bg-button-selector' : undefined
			}
		/>
	);
}
